import type { EventDateType } from '~/composables/types/event'

export interface DateRequestFormData {
  email: string
  phone: string
  name: string
  surname: string
  numberOfTickets: number | null
  companyName: string
  city: string
  dateType: EventDateType | null
  dates: string[]
  additionalInfo?: string
}

const DEFAULT_FORM_DATA: DateRequestFormData = {
  email: '',
  phone: '',
  name: '',
  surname: '',
  numberOfTickets: null,
  companyName: '',
  city: '',
  dateType: null,
  dates: [],
  additionalInfo: '',
}

const DATE_REQUEST_FORM_STORAGE_KEY = 'date-request-form-data'

const isFormStorageAvailable = () =>
  import.meta.client && typeof window !== 'undefined' && window.sessionStorage

export const useDateRequestFormStorage = () => {
  // Ticket count is session-only. We still need three touches:
  // 1) getPersistableSnapshot — useKftLocalStorage would otherwise keep writing it.
  // 2) watch(loaded) — legacy JSON may still include it; parse puts it on state once.
  // 3) useDateRequestFormState — form always sets numberOfTickets to null after ...data.
  const { data: savedFormData, loaded } =
    useKftLocalStorage<DateRequestFormData>(
      DATE_REQUEST_FORM_STORAGE_KEY,
      DEFAULT_FORM_DATA,
      {
        storage: 'session',
        getPersistableSnapshot: (value) => {
          const { numberOfTickets: _, ...rest } = value
          return rest
        },
      }
    )

  watch(loaded, (isLoaded) => {
    if (!isLoaded) {
      return
    }
    savedFormData.value = { ...savedFormData.value, numberOfTickets: null }
  })

  const saveFormData = (formData: Partial<DateRequestFormData>) => {
    if (!isFormStorageAvailable()) {
      return
    }

    try {
      savedFormData.value = {
        ...savedFormData.value,
        ...formData,
      }
    } catch (error) {
      console.error(
        'Error saving date request form data to session storage',
        error
      )
    }
  }

  const saveFormDateFromCalendar = (date: string) => {
    if (!isFormStorageAvailable()) {
      return
    }

    const newFormData: DateRequestFormData = {
      ...savedFormData.value,
      dates: [date, ...savedFormData.value.dates].slice(0, 3),
    }

    saveFormData(newFormData)
  }

  const getInitialFormData = (): DateRequestFormData => {
    // Always return the current savedFormData value, which will be updated
    // when session storage data is loaded
    return savedFormData.value
  }

  return {
    savedFormData,
    loaded,
    saveFormData,
    saveFormDateFromCalendar,
    getInitialFormData,
  }
}
